import { computed } from "vue";
import { useDashboardTab } from "@/composables/admin/dashboard/useDashboardTab";
import { getFans } from "@/services/dashboard/dashboard";
import { getSeasonName } from "@/composables/admin/dashboard/seasonUtils";

/**
 * Paleta de colores por rango etario.
 * Cada rango tiene un color sólido para Abonados y un tono más claro para Compradores.
 * @type {Array<{abonados: string, compradores: string}>}
 */
const AGE_PALETTE = [
    { abonados: "#2563eb", compradores: "#60a5fa" }, // 0: blue
    { abonados: "#ef4444", compradores: "#fca5a5" }, // 1: red
    { abonados: "#10b981", compradores: "#6ee7b7" }, // 2: green
    { abonados: "#f59e0b", compradores: "#fcd34d" }, // 3: amber
    { abonados: "#8b5cf6", compradores: "#c4b5fd" }, // 4: purple
    { abonados: "#06b6d4", compradores: "#67e8f9" }, // 5: cyan
    { abonados: "#f97316", compradores: "#fdba74" }, // 6: orange
    { abonados: "#6366f1", compradores: "#a5b4fc" }, // 7: indigo
    { abonados: "#14b8a6", compradores: "#5eead4" }, // 8: teal
    { abonados: "#ec4899", compradores: "#f9a8d4" }, // 9: pink
];

/**
 * Composable del panel de afición (fans) del dashboard.
 * Muestra distribución por edad, tipo de comprador, geografía y desglose de asistencia.
 *
 * @param {import('vue').Ref<number[]>} seasonIdsRef
 * @param {import('vue').Ref<Array>} seasonsRef
 * @returns {{
 *     data: import('vue').Ref<object|null>,
 *     loading: import('vue').Ref<boolean>,
 *     error: import('vue').Ref<string|null>,
 *     isMultiSeason: import('vue').ComputedRef<boolean>,
 *     ageChartData: import('vue').ComputedRef<Array>,
 *     buyerTypeDonutData: import('vue').ComputedRef<Array>,
 *     geographyTables: import('vue').ComputedRef<{ provincias: Array, localidades: Array, codigos_postales: Array }>,
 *     attendanceBreakdown: import('vue').ComputedRef<object|null>,
 *     seasonBreakdown: import('vue').ComputedRef<Array|null>,
 *     refresh: () => Promise<void>,
 * }}
 */
export function useFans(seasonIdsRef, seasonsRef) {
    const base = useDashboardTab(seasonIdsRef, seasonsRef, getFans);

    /**
     * Distribución por edad (abonados + compradores).
     * Los rangos de edad son categorías del eje X. Abonados y Compradores son series
     * agrupadas. Cada barra tiene su propio color vía columnas {role: 'style'} para
     * que cada rango etario tenga un color distinguible.
     *
     * Estructura: [['Edad', 'Abonados', {role:'style'}, 'Compradores', {role:'style'}]]
     */
    const ageChartData = computed(() => {
        const perSeason = base.data.value?.data?.per_season || {};
        const entries = Object.entries(perSeason).filter(
            ([, entry]) => entry?.age_distribution,
        );
        if (entries.length === 0) return [];

        // Recolectar todos los rangos de edad
        const allRanges = new Set();
        const seasonData = {};
        for (const [seasonId, entry] of entries) {
            const name = getSeasonName(seasonsRef.value, seasonId);
            const dist = entry.age_distribution || {};
            const abonados = dist.abonados || {};
            const compradores = dist.compradores || {};
            seasonData[name] = { abonados, compradores };
            Object.keys(abonados).forEach(k => allRanges.add(k));
            Object.keys(compradores).forEach(k => allRanges.add(k));
        }
        if (allRanges.size === 0) return [];
        const sortedRanges = Array.from(allRanges).sort();

        if (base.isMultiSeason.value) {
            // Cada (temporada, rango) tiene su propio par de colores.
            // Se indexa la paleta secuencialmente: seasonIdx * totalRanges + ageIdx
            const seasonNames = entries.map(([sid]) => getSeasonName(seasonsRef.value, sid));
            const header = ["Edad"];
            for (const name of seasonNames) {
                header.push(`${name} - Abonados`, { role: "style" });
                header.push(`${name} - Compradores`, { role: "style" });
            }
            const totalRanges = sortedRanges.length;
            const rows = sortedRanges.map((range, ageIdx) => {
                const row = [range];
                for (let sIdx = 0; sIdx < seasonNames.length; sIdx++) {
                    const sd = seasonData[seasonNames[sIdx]];
                    const paletteIdx = (sIdx * totalRanges + ageIdx) % AGE_PALETTE.length;
                    const colors = AGE_PALETTE[paletteIdx];
                    row.push(sd.abonados[range] ?? 0, colors.abonados);
                    row.push(sd.compradores[range] ?? 0, colors.compradores);
                }
                return row;
            });
            return [header, ...rows];
        }

        // Single season: dos series (Abonados, Compradores) con colores por barra
        const dist = entries[0][1].age_distribution || {};
        const abonados = dist.abonados || {};
        const compradores = dist.compradores || {};
        const header = ["Edad", "Abonados", { role: "style" }, "Compradores", { role: "style" }];
        const rows = sortedRanges.map((range, idx) => {
            const colors = AGE_PALETTE[idx % AGE_PALETTE.length];
            return [
                range,
                abonados[range] ?? 0,
                colors.abonados,
                compradores[range] ?? 0,
                colors.compradores,
            ];
        });
        return [header, ...rows];
    });

    /**
     * Datos para donut de tipos de comprador (recurrentes vs nuevos).
     */
    const buyerTypeDonutData = computed(() => {
        const perSeason = base.data.value?.data?.per_season || {};
        const entries = Object.entries(perSeason).filter(
            ([, entry]) => entry?.buyer_types,
        );
        if (entries.length === 0) return [];

        if (base.isMultiSeason.value) {
            const header = ["Temporada", "Recurrentes", "Nuevos"];
            const rows = entries.map(([seasonId, entry]) => {
                const bt = entry.buyer_types || {};
                return [
                    getSeasonName(seasonsRef.value, seasonId),
                    bt.recurrentes?.total || 0,
                    bt.nuevos?.total || 0,
                ];
            });
            return [header, ...rows];
        }

        const bt = entries[0][1].buyer_types || {};
        return [
            ["Tipo", "Total"],
            ["Recurrentes", bt.recurrentes?.total || 0],
            ["Nuevos", bt.nuevos?.total || 0],
        ];
    });

    /**
     * Tablas de geografía (provincias, localidades, códigos_postales).
     * Agrega datos de todas las temporadas sumando totales.
     */
    const geographyTables = computed(() => {
        const perSeason = base.data.value?.data?.per_season || {};
        const geo = { provincias: [], localidades: [], codigos_postales: [] };

        for (const [, entry] of Object.entries(perSeason)) {
            const g = entry?.geography;
            if (!g) continue;
            for (const [dim, items] of Object.entries(g)) {
                if (!Array.isArray(items) || !geo[dim]) continue;
                for (const item of items) {
                    const existing = geo[dim].find(e => e.label === item.label);
                    if (existing) {
                        existing.total += item.total || 0;
                    } else {
                        geo[dim].push({ label: item.label, total: item.total || 0 });
                    }
                }
            }
        }

        // Sort by total desc, take top 10
        for (const dim of Object.keys(geo)) {
            geo[dim].sort((a, b) => b.total - a.total);
            geo[dim] = geo[dim].slice(0, 10);
        }

        return geo;
    });

    /**
     * Desglose de asistencia: abonado_pct, entrada_pct, invitacion_pct.
     */
    const attendanceBreakdown = computed(() => {
        const perSeason = base.data.value?.data?.per_season || {};
        const entries = Object.entries(perSeason).filter(
            ([, entry]) => entry?.attendance_breakdown,
        );
        if (entries.length === 0) return null;

        // Single: use the only entry
        const breakdown = entries[0][1].attendance_breakdown;
        if (!breakdown) return null;

        return {
            clubName: breakdown.club_name || "",
            abonadoPct: Number(breakdown.abonado_pct || 0),
            entradaPct: Number(breakdown.entrada_pct || 0),
            invitacionPct: Number(breakdown.invitacion_pct || 0),
            mediaAbonado: Number(breakdown.media_abonado || 0),
            mediaEntrada: Number(breakdown.media_entrada || 0),
            mediaInvitacion: Number(breakdown.media_invitacion || 0),
        };
    });

    /**
     * Desglose de asistencia comparativo por temporada (multi-season).
     * Retorna array con datos de cada temporada, o null si < 2 temporadas.
     */
    const attendanceComparison = computed(() => {
        const perSeason = base.data.value?.data?.per_season || {};
        const entries = Object.entries(perSeason).filter(
            ([, entry]) => entry?.attendance_breakdown,
        );
        if (entries.length < 2) return null;

        return entries.map(([seasonId, entry]) => {
            const b = entry.attendance_breakdown;
            return {
                season: getSeasonName(seasonsRef.value, seasonId),
                abonadoPct: Number(b.abonado_pct || 0),
                entradaPct: Number(b.entrada_pct || 0),
                invitacionPct: Number(b.invitacion_pct || 0),
                mediaAbonado: Number(b.media_abonado || 0),
                mediaEntrada: Number(b.media_entrada || 0),
                mediaInvitacion: Number(b.media_invitacion || 0),
            };
        });
    });

    /**
     * Deltas de asistencia cuando son exactamente 2 temporadas.
     */
    const attendanceDelta = computed(() => {
        const comp = attendanceComparison.value;
        if (!comp || comp.length !== 2) return null;
        // última - primera
        return {
            abonadoPct: comp[1].abonadoPct - comp[0].abonadoPct,
            entradaPct: comp[1].entradaPct - comp[0].entradaPct,
            invitacionPct: comp[1].invitacionPct - comp[0].invitacionPct,
            mediaAbonado: comp[1].mediaAbonado - comp[0].mediaAbonado,
            mediaEntrada: comp[1].mediaEntrada - comp[0].mediaEntrada,
            mediaInvitacion: comp[1].mediaInvitacion - comp[0].mediaInvitacion,
        };
    });

    /**
     * Desglose por temporada.
     */
    const seasonBreakdown = computed(() => {
        const perSeason = base.data.value?.data?.per_season || {};
        const entries = Object.entries(perSeason).filter(
            ([, entry]) => entry?.buyer_types,
        );
        if (entries.length === 0) return null;

        return entries.map(([seasonId, entry]) => {
            const bt = entry.buyer_types || {};
            const att = entry.attendance_breakdown || {};
            return {
                season: getSeasonName(seasonsRef.value, seasonId),
                recurrentes: bt.recurrentes?.total || 0,
                nuevos: bt.nuevos?.total || 0,
                abonadoPct: Number(att.abonado_pct || 0),
                entradaPct: Number(att.entrada_pct || 0),
                invitacionPct: Number(att.invitacion_pct || 0),
            };
        });
    });

    /** Opciones para el chart de edades */
    const ageChartOptions = {
        legend: "none",
        chartArea: { width: "80%", height: "65%" },
        annotations: {
            textStyle: { fontSize: 11, bold: true },
            alwaysOutside: true,
        },
    };

    /** Opciones para donut de tipos de comprador */
    const donutChartOptions = {
        pieHole: 0.5,
        legend: { position: "bottom" },
        chartArea: { width: "80%", height: "70%" },
        colors: ["#2563eb", "#059669"],
    };

    /** Tipo de chart: ColumnChart para multi-season, PieChart para single */
    const buyerChartType = computed(() =>
        base.isMultiSeason.value ? "ColumnChart" : "PieChart",
    );

    return {
        data: base.data,
        loading: base.loading,
        error: base.error,
        isMultiSeason: base.isMultiSeason,
        ageChartData,
        buyerTypeDonutData,
        geographyTables,
        attendanceBreakdown,
        attendanceComparison,
        attendanceDelta,
        seasonBreakdown,
        ageChartOptions,
        donutChartOptions,
        buyerChartType,
        refresh: base.refresh,
    };
}
